import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { FaUserTimes, FaDownload, FaFilter, FaCalendarAlt, FaEnvelope, FaPhone } from 'react-icons/fa';

interface NoShowRecord {
  rsvp_id: number;
  event_id: string;
  user_id: number;
  member_name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  chapter_name: string;
  event_title: string;
  event_date: string;
  venue: string;
  rsvp_status: string;
  rsvp_created_at: string;
}

interface Chapter {
  id: number;
  name: string;
}

const NoShowReport: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [noShows, setNoShows] = useState<NoShowRecord[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const url = selectedChapter !== 'all' 
        ? `https://rmbbackend.rmbjaipur.com/auth/reports/no-shows?chapter_id=${selectedChapter}`
        : `https://rmbbackend.rmbjaipur.com/auth/reports/no-shows`;

      const res = await axios.get(url);
      if (res.data.success) {
        setNoShows(res.data.noShows || []);
      }
    } catch {
      setError('Failed to load no-show report');
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/chapters');
      if (res.data.success) {
        setChapters(res.data.chapters || []);
      }
    } catch {
      // Ignore
    }
  };

  useEffect(() => {
    fetchChapters();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [selectedChapter]);

  const handleExportCSV = () => {
    if (noShows.length === 0) return;

    const headers = ['Member Name', 'Email', 'Phone', 'Company', 'Chapter', 'Event Title', 'Event Date', 'RSVP Status'];
    const rows = noShows.map(n => [
      `"${n.member_name || ''}"`,
      `"${n.email || ''}"`,
      `"${n.number || ''}"`,
      `"${n.company_name || ''}"`,
      `"${n.chapter_name || ''}"`,
      `"${n.event_title || ''}"`,
      `"${n.event_date ? new Date(n.event_date).toLocaleDateString() : ''}"`,
      `"${n.rsvp_status || ''}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `RSVP_No_Show_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`p-6 ${isLight ? 'bg-slate-50 text-slate-900' : 'bg-[#060D1A] text-slate-100'} rounded-2xl shadow-xl min-h-[600px]`}>
      
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-200/20">
        <div>
          <h2 className="text-2xl font-black flex items-center gap-2 text-rose-500">
            <FaUserTimes /> Event RSVP No-Show Report
          </h2>
          <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'} mt-1`}>
            List of members who confirmed attendance (RSVP "Attending") for past events but failed to show up.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Chapter Selector Filter */}
          <div className="flex items-center gap-2 bg-slate-500/10 px-3 py-2 rounded-xl border border-slate-500/20">
            <FaFilter className="text-amber-500 text-xs" />
            <select
              value={selectedChapter}
              onChange={e => setSelectedChapter(e.target.value)}
              className="bg-transparent text-xs font-semibold focus:outline-none cursor-pointer"
            >
              <option value="all">All Chapters</option>
              {chapters.map(c => (
                <option key={c.id} value={c.id} className="text-slate-900">{c.name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleExportCSV}
            disabled={noShows.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-lg transition-all"
          >
            <FaDownload /> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs rounded-xl">
          {error}
        </div>
      )}

      {/* Stats Ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
        <div className={`p-4 rounded-xl border ${isLight ? 'bg-white border-slate-200' : 'bg-[#0b172a] border-slate-800'}`}>
          <span className="text-xs text-slate-400 font-medium">Total No-Show Cases</span>
          <p className="text-2xl font-black text-rose-500 mt-1">{noShows.length}</p>
        </div>
        <div className={`p-4 rounded-xl border ${isLight ? 'bg-white border-slate-200' : 'bg-[#0b172a] border-slate-800'}`}>
          <span className="text-xs text-slate-400 font-medium">Selected Chapter</span>
          <p className="text-lg font-bold text-amber-500 mt-1">
            {selectedChapter === 'all' ? 'All Chapters' : chapters.find(c => String(c.id) === selectedChapter)?.name || 'Selected Chapter'}
          </p>
        </div>
        <div className={`p-4 rounded-xl border ${isLight ? 'bg-white border-slate-200' : 'bg-[#0b172a] border-slate-800'}`}>
          <span className="text-xs text-slate-400 font-medium">Audit Status</span>
          <p className="text-xs font-semibold text-emerald-400 mt-2">Locked Attendance Records Audit</p>
        </div>
      </div>

      {/* No Show Table */}
      {loading ? (
        <div className="py-20 text-center text-slate-400 text-sm">Loading report data...</div>
      ) : noShows.length === 0 ? (
        <div className="py-20 text-center text-slate-400 text-sm">🎉 Excellent! No unexcused RSVP no-shows found for the selected filter.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-500/20">
          <table className="w-full text-left text-xs">
            <thead className={`uppercase text-[10px] font-bold ${isLight ? 'bg-slate-200/70 text-slate-700' : 'bg-slate-800/70 text-slate-300'}`}>
              <tr>
                <th className="p-3.5">#</th>
                <th className="p-3.5">Member Details</th>
                <th className="p-3.5">Chapter</th>
                <th className="p-3.5">Event Title & Date</th>
                <th className="p-3.5">RSVP Status</th>
                <th className="p-3.5">Attendance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-500/10">
              {noShows.map((n, idx) => (
                <tr key={idx} className={`hover:bg-slate-500/5 transition-colors`}>
                  <td className="p-3.5 text-slate-400 font-mono">{idx + 1}</td>
                  <td className="p-3.5">
                    <div className="font-bold text-slate-100">{n.member_name}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-3 mt-0.5">
                      {n.email && <span className="flex items-center gap-1"><FaEnvelope className="text-[9px]" /> {n.email}</span>}
                      {n.number && <span className="flex items-center gap-1"><FaPhone className="text-[9px]" /> {n.number}</span>}
                    </div>
                    {n.company_name && <div className="text-[10px] text-amber-500 mt-0.5">{n.company_name}</div>}
                  </td>
                  <td className="p-3.5 font-semibold text-slate-300">{n.chapter_name}</td>
                  <td className="p-3.5">
                    <div className="font-bold">{n.event_title}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                      <FaCalendarAlt className="text-[10px] text-amber-500" />
                      {n.event_date ? new Date(n.event_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </div>
                  </td>
                  <td className="p-3.5">
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                      RSVP: {n.rsvp_status}
                    </span>
                  </td>
                  <td className="p-3.5">
                    <span className="px-2.5 py-1 text-[10px] font-black uppercase rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30 flex items-center gap-1 w-max">
                      <FaUserTimes /> ABSENT (No Show)
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NoShowReport;
